import { EstimateType, NOTE_TYPE_TO_ESTIMATE_TYPE, getNoteTypeFromEstimateType, NoteType, type RateCard } from "@rebuild/shared";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useEffect, useMemo } from "react";
import type { EstimateUploadState } from "./UploadEstimationFiles";

type NoteTypeDropdownOption = { value: NoteType; label: string; disabled?: boolean; disabledHint?: string };

export { getNoteTypeFromEstimateType } from "@rebuild/shared";

export const EstimateTypeDropdown = ({
  mitigationEnabled,
  contentsEnabled,
  moldEnabled,
  fireMitigationEnabled,
  biohazardEnabled,
  timeAndMaterialEnabled,
  rateCards,
  estimateId,
  estimateType,
  onUpdate,
  noteType,
  setNoteType,
}: {
  mitigationEnabled: boolean;
  contentsEnabled: boolean;
  moldEnabled: boolean;
  fireMitigationEnabled: boolean;
  biohazardEnabled: boolean;
  timeAndMaterialEnabled: boolean;
  rateCards: RateCard[];
  estimateId: string;
  estimateType: EstimateType | undefined;
  onUpdate: (updates: Partial<EstimateUploadState>) => void;
  noteType: NoteType;
  setNoteType: (noteType: NoteType) => void;
}) => {
  // Set default to MITIGATION when component mounts if no type is set
  useEffect(() => {
    if (!estimateType) {
      onUpdate({ estimateType: EstimateType.MITIGATION });
      setNoteType(getNoteTypeFromEstimateType(EstimateType.MITIGATION));
    }
  }, [estimateId, estimateType, onUpdate]);

  const handleTypeChange = (noteTypeValue: string) => {
    const newNoteType = noteTypeValue as NoteType;
    setNoteType(newNoteType);

    // Map note type to estimate type using the mapping
    const estimateType = NOTE_TYPE_TO_ESTIMATE_TYPE[newNoteType] || EstimateType.MITIGATION;

    onUpdate({ estimateType, noteType: newNoteType });
  };

  const dropDownOptions = useMemo(() => {
    const options: NoteTypeDropdownOption[] = [
      {
        value: NoteType.INTERIOR_RECONSTRUCTION,
        label: "Interior Reconstruction",
      },
      {
        value: NoteType.EXTERIOR_RECONSTRUCTION,
        label: "Exterior Reconstruction",
      },
    ];

    if (mitigationEnabled) {
      options.push({
        value: NoteType.MITIGATION,
        label: "Mitigation",
      });
    }
    if (contentsEnabled) {
      options.push({
        value: NoteType.CONTENTS,
        label: "Contents",
      });
    }

    if (moldEnabled) {
      options.push({
        value: NoteType.MOLD,
        label: "Mold",
      });
    }

    if (fireMitigationEnabled) {
      options.push({
        value: NoteType.FIRE_MITIGATION,
        label: "Fire Mitigation",
      });
    }

    if (biohazardEnabled) {
      options.push({
        value: NoteType.BIOHAZARD,
        label: "Biohazard",
      });
    }

    if (timeAndMaterialEnabled) {
      const hasNoRateCards = rateCards.length === 0;
      options.push({
        value: NoteType.TIME_AND_MATERIAL,
        label: "Time and Material",
        disabled: hasNoRateCards,
        disabledHint: hasNoRateCards ? "No rate cards — create one in Tools" : undefined,
      });
    }

    return options;
  }, [mitigationEnabled, contentsEnabled, moldEnabled, fireMitigationEnabled, biohazardEnabled, timeAndMaterialEnabled, rateCards.length]);

  const getDisplayName = (noteType: NoteType) => {
    return dropDownOptions.find((option) => option.value === noteType)?.label;
  };

  return (
    <Select value={noteType} onValueChange={handleTypeChange}>
      <SelectTrigger className="h-8 w-auto px-3 text-sm">
        <SelectValue placeholder={getDisplayName(noteType)} />
      </SelectTrigger>
      <SelectContent>
        {dropDownOptions.map(({ value, label, disabled, disabledHint }) => (
          <SelectItem key={value} value={value} className="py-3" disabled={disabled}>
            <span className="flex items-center gap-2">
              {label}
              {disabled && disabledHint && <span className="text-xs text-muted-foreground">{disabledHint}</span>}
            </span>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
};
